import type {
    IMediaCacheController,
    IMediaURLResolver,
    MediaBinaryStorageTypeMap,
} from '../interface/cache';
import type { IMediaCacheSource } from './cache.source';
import cacheSource from './cache.source';

async function _isCachedMedium(mediaId: string, cacheList: IMediaCacheSource[]): Promise<boolean> {
    for (const cc of cacheList) {
        const isCached = await cc.isCached(mediaId);
        if (isCached) {
            return true;
        }
    }

    return false;
}

async function _isCachedMedia(
    mediaIds: string[],
    cacheList: IMediaCacheSource[],
): Promise<boolean> {
    for (const mediaId of mediaIds) {
        const isCached = await _isCachedMedium(mediaId, cacheList);
        if (!isCached) {
            return false;
        }
    }

    return true;
}

async function _cachedResponseMedium<
    T extends keyof MediaBinaryStorageTypeMap,
    U extends MediaBinaryStorageTypeMap[T],
>(mediaId: string, cacheList: IMediaCacheSource[], type: T): Promise<U> {
    for (const cacheSource of cacheList) {
        const hasAsset = await cacheSource.isCached(mediaId);

        if (hasAsset) {
            return cacheSource.cachedResponse(mediaId, type);
        }
    }

    throw new Error(`No media item found for id: ${mediaId}`);
}

function cacheController(): IMediaCacheController {
    let cacheList: IMediaCacheSource[] = [];

    return {
        addCacheResolver: (id: string, resolver: IMediaURLResolver) => {
            cacheList.push(cacheSource(id, resolver));
        },
        removeCacheResolver: (id: string) => {
            const matchingControllers = cacheList.filter(
                cacheController => cacheController.cacheName() === id,
            );
            cacheList = cacheList.filter(cacheItem => !matchingControllers.includes(cacheItem));
        },
        isMediumCached: (mediaId: string) => _isCachedMedium(mediaId, cacheList),
        isAllMediaCached: (mediaIds: string[]) => _isCachedMedia(mediaIds, cacheList),
        removeMediumCache: async (mediaId: string) => {
            await Promise.all(cacheList.map(cc => cc.removeFromCache(mediaId)));
        },
        cachedResponse: <
            T extends keyof MediaBinaryStorageTypeMap,
            U extends MediaBinaryStorageTypeMap[T],
        >(
            mediaId: string,
            type: T,
        ): Promise<U> => _cachedResponseMedium(mediaId, cacheList, type),
    };
}

export default cacheController;
